import React, { useEffect, useState } from 'react';
import Icon from '../../shared/Icon';
import ProposalItemsTable from '../ProposalItemsTable';
import { UseProposalWorkspaceResult } from '../../../hooks/useProposalWorkspace';
import { WorkspaceActions } from '../ProposalWorkspace';
import { SURFACE_CENTRAL } from '../../../utils/statusColors';
import { fmtDateTime } from '../../../utils/formatters';
import { LABEL_AGRUPAMENTO } from '../procurementUi';
import { procurementService } from '../../../services/api';
import { campo as receitaDoCampo } from '../../../design';

/**
 * Estágio MONTAGEM — onde a proposta é construída: contexto da licitação
 * (colapsado, porque é referência e não trabalho) e a tabela de itens/preços,
 * que é o trabalho de verdade.
 *
 * No editor antigo os dados da licitação ficavam num card grande e SEMPRE
 * aberto no topo da aba, empurrando os itens (o que o usuário veio fazer) pra
 * baixo da dobra.
 *
 * Os dados da licitação também são EDITÁVEIS aqui (21/08/2026). Antes eram só
 * leitura, e o único formulário que os editava vivia no drawer "Nova Proposta",
 * que saiu da tela Cadastrar no cutover do workspace — resultado: licitação já
 * cadastrada com processo/UASG/data errada só se corrigia no banco (aconteceu
 * no COREN-PE 22/2026, com o pregoeiro pedindo reenvio da proposta).
 */

interface Props {
  ws: UseProposalWorkspaceResult;
  actions: WorkspaceActions;
  fmt: (v: any) => string;
  catserOptions: { code: string; label: string }[];
  /** Já há uma operação rodando neste item — trava os botões que a disparam. */
  busy?: boolean;
}

/** ISO → "YYYY-MM-DDTHH:mm" em BRT: datetime-local não carrega timezone. */
const toLocalInput = (v: any): string => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  // en-CA dá "YYYY-MM-DD, HH:MM" — vira "YYYY-MM-DDTHH:MM"
  return d.toLocaleString('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).replace(', ', 'T');
};

type CampoEditavel =
  | 'numeroCompra' | 'anoCompra' | 'processoSei' | 'codigoUnidade'
  | 'orgaoCnpj' | 'criterioJulgamento'
  | 'dataAbertura' | 'dataEncerramento' | 'dataSessaoInicio';

type Draft = Record<CampoEditavel, string>;

const draftFromItem = (item: any): Draft => ({
  numeroCompra: item?.numeroCompra || '',
  anoCompra: item?.anoCompra ? String(item.anoCompra) : '',
  processoSei: item?.processoSei || '',
  codigoUnidade: item?.codigoUnidade || '',
  orgaoCnpj: item?.orgaoCnpj || '',
  criterioJulgamento: item?.criterioJulgamento || '',
  dataAbertura: toLocalInput(item?.dataAbertura),
  dataEncerramento: toLocalInput(item?.dataEncerramento),
  dataSessaoInicio: toLocalInput(item?.dataSessaoInicio),
});

export default function MontagemSection({ ws, actions, fmt, catserOptions, busy }: Props) {
  const { item, items } = ws;
  const hasItems = items.length > 0;
  // Item sem valor estimado = o portal ainda não foi lido (ou leu incompleto).
  const semValorEstimado = hasItems && items.every((it: any) => !(Number(it.estimatedPrice) > 0));

  const [editando, setEditando] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFromItem(item));
  const [salvando, setSalvando] = useState<CampoEditavel | null>(null);

  // O `item` vem da lista viva: depois do reload (ou ao trocar de proposta) o
  // rascunho precisa acompanhar, senão o campo segue mostrando o valor antigo.
  // Enquanto o usuário edita, o rascunho manda — um repoll no meio da digitação
  // não pode reverter o que ele está escrevendo.
  useEffect(() => {
    if (!editando) setDraft(draftFromItem(item));
  }, [item, editando]);

  const meta: Array<[string, any]> = [
    ['Modalidade', item?.modalidadeNome],
    ['Critério', item?.criterioJulgamento],
    ['Modo de disputa', item?.modoDisputa],
    ['Processo', item?.processoSei],
    ['UASG', item?.codigoUnidade],
    ['CNPJ do órgão', item?.orgaoCnpj],
    ['Abertura', item?.dataAbertura ? fmtDateTime(item.dataAbertura) : null],
    ['Encerramento', item?.dataEncerramento ? fmtDateTime(item.dataEncerramento) : null],
    ['Sessão pública', item?.dataSessaoInicio ? fmtDateTime(item.dataSessaoInicio) : null],
    ['Valor estimado', item?.valorEstimado ? `R$ ${fmt(item.valorEstimado)}` : null],
  ];

  /** Grava UM campo e recarrega o item (a fonte é a lista, não o rascunho). */
  const salvarCampo = async (campo: CampoEditavel, patch: any) => {
    if (!item?.id) return;
    setSalvando(campo);
    try {
      await procurementService.updateItemDetails(item.id, patch);
      await ws.reload();
      actions.showToast('Dados da licitação atualizados');
    } catch (e: any) {
      actions.showToast('Erro ao salvar: ' + (e?.response?.data?.message || e?.message), 'error');
      setDraft(draftFromItem(item)); // não gravou: o campo volta ao que está no banco
    } finally {
      setSalvando(null);
    }
  };

  /** Salva no blur, e só quando o valor MUDOU (evita PATCH a cada tab). */
  const onBlurCampo = (campo: CampoEditavel) => {
    const original = draftFromItem(item);
    const valor = draft[campo];
    if (valor === original[campo]) return;
    switch (campo) {
      case 'numeroCompra':
      case 'anoCompra': {
        // Manda "número/ano" — o backend (splitNumeroAno) separa e grava certo.
        const num = draft.numeroCompra.trim();
        const ano = draft.anoCompra.trim();
        salvarCampo(campo, { numeroCompra: ano ? `${num}/${ano}` : num });
        break;
      }
      case 'orgaoCnpj':
        salvarCampo(campo, { orgaoCnpj: valor.replace(/[^\d]/g, '') });
        break;
      default:
        // String vazia (e não undefined) para que limpar o campo LIMPE no banco.
        salvarCampo(campo, { [campo]: valor });
    }
  };

  // Sem largura própria: quem usa decide (`w-full` no grid, flex-1/w-16 no par
  // número+ano). Um `w-full` aqui venceria o `w-16` do ano na cascata do
  // Tailwind e espremia o campo do número até zerar.
  const campoCls = `rounded-lg px-2 py-1.5 text-xs outline-none transition-colors ${receitaDoCampo}`;

  const campo = (label: string, nome: CampoEditavel, extra?: { type?: string; placeholder?: string; hint?: string; maxLength?: number }) => (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 flex items-center gap-1">
        {label}
        {salvando === nome && <Icon name="refresh" className="w-2.5 h-2.5 animate-spin text-slate-400" />}
      </div>
      <input
        type={extra?.type || 'text'}
        value={draft[nome]}
        maxLength={extra?.maxLength}
        placeholder={extra?.placeholder}
        onChange={(e) => setDraft((prev) => ({ ...prev, [nome]: e.target.value }))}
        onBlur={() => onBlurCampo(nome)}
        className={`${campoCls} w-full`}
      />
      {extra?.hint && <div className="text-[9px] text-slate-500 mt-0.5">{extra.hint}</div>}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Contexto da licitação — referência, começa fechado */}
      <details className={`${SURFACE_CENTRAL.card} overflow-hidden`}>
        <summary className="px-4 py-3 cursor-pointer text-sm text-slate-300 font-medium hover:text-white transition-colors">
          Dados da licitação
          {item?.objetoCompra && (
            <span className="block text-[11px] text-slate-500 font-normal mt-0.5 line-clamp-1">
              {item.objetoCompra}
            </span>
          )}
        </summary>
        <div className="px-4 pb-4 space-y-3">
          {item?.objetoCompra && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Objeto</div>
              <p className="text-xs text-slate-300 leading-relaxed">{item.objetoCompra}</p>
            </div>
          )}

          {editando ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5 flex items-center gap-1">
                    Nº da compra <span className="text-slate-600 normal-case">(número / ano)</span>
                    {(salvando === 'numeroCompra' || salvando === 'anoCompra') && (
                      <Icon name="refresh" className="w-2.5 h-2.5 animate-spin text-slate-400" />
                    )}
                  </div>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={draft.numeroCompra}
                      onChange={(e) => setDraft((prev) => ({ ...prev, numeroCompra: e.target.value }))}
                      onBlur={() => onBlurCampo('numeroCompra')}
                      placeholder="90052"
                      className={`${campoCls} flex-1 min-w-0`}
                    />
                    <input
                      type="text"
                      value={draft.anoCompra}
                      onChange={(e) => setDraft((prev) => ({ ...prev, anoCompra: e.target.value }))}
                      onBlur={() => onBlurCampo('anoCompra')}
                      placeholder="ano"
                      maxLength={4}
                      title="Ano da licitação"
                      className={`${campoCls} w-16 shrink-0 text-center`}
                    />
                  </div>
                  <div className="text-[9px] text-slate-500 mt-0.5">É este número que sai no cabeçalho do PDF da proposta.</div>
                </div>
                {campo('Processo', 'processoSei', { placeholder: '200.083808/2025', hint: 'Processo SEI / administrativo.' })}
                {campo('UASG', 'codigoUnidade', { placeholder: '925129' })}
                {campo('CNPJ do órgão', 'orgaoCnpj', { placeholder: '06.279.103/0001-19', hint: 'Usado na busca do PNCP.' })}
                {campo('Critério', 'criterioJulgamento', { placeholder: 'MENOR PREÇO' })}
                {campo('Abertura', 'dataAbertura', { type: 'datetime-local' })}
                {campo('Encerramento', 'dataEncerramento', { type: 'datetime-local' })}
                {campo('Sessão pública', 'dataSessaoInicio', { type: 'datetime-local', hint: 'Início da disputa/lances.' })}
              </div>
              <div className="text-[10px] text-slate-500">
                Salva sozinho ao sair de cada campo. Modalidade, modo de disputa e valor estimado vêm do portal —
                para corrigi-los use “Sincronizar do portal” ou “Atualizar dados pelo PNCP”, que também podem
                sobrescrever o que for editado aqui.
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
              {meta.filter(([, v]) => !!v).map(([label, value]) => (
                <div key={label}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
                  <div className="text-xs text-slate-300 truncate" title={String(value)}>{String(value)}</div>
                </div>
              ))}
            </div>
          )}

          <div className="pt-1 flex items-center gap-4 flex-wrap">
            <button
              onClick={() => {
                if (!editando) setDraft(draftFromItem(item));
                setEditando(!editando);
              }}
              disabled={!item?.id}
              className="text-[11px] text-slate-400 hover:text-white disabled:opacity-40 transition-colors flex items-center gap-1"
              title={editando ? 'Voltar para a visualização' : 'Corrigir número, processo, UASG, CNPJ e datas'}
            >
              <Icon name={editando ? 'check' : 'edit'} className="w-3 h-3" />
              {editando ? 'Concluir edição' : 'Editar dados'}
            </button>
            <button
              onClick={actions.onSyncPncp}
              className="text-[11px] text-slate-400 hover:text-white transition-colors flex items-center gap-1"
            >
              <Icon name="refresh" className="w-3 h-3" /> Atualizar dados pelo PNCP
            </button>
          </div>
        </div>
      </details>

      {/* Itens — o trabalho */}
      {hasItems ? (
        <div className={`${SURFACE_CENTRAL.card} p-4`}>
          {/* 21/10/2026 (Heitor): o "Sincronizar do portal" da seção saiu. Era a mesma ação do topo (o botão principal
              quando nada está precificado, e o ⋯ do cabeçalho sempre), e durante a sincronização a tela dizia
              "Sincronizando" três vezes: no topo, na barra e aqui. O aviso de item sem estimado fica e aponta o topo. */}
          {semValorEstimado && (
            <div className="text-[11px] text-slate-400 mb-3" data-aviso-sem-estimado>
              ⚠️ Os itens estão sem valor estimado: use “Sincronizar do portal”, no topo, para trazer os valores.
            </div>
          )}
          <ProposalItemsTable
            items={items as any}
            onChange={(next) => ws.setItems(next as any)}
            contractMonths={ws.contractMonths}
            onContractMonthsChange={(v) => ws.setForm({ contractMonths: v })}
            fmt={fmt}
            catserOptions={catserOptions}
            canAdequar={!!ws.proposal?.id}
            onAdequar={actions.onAdequar}
            onClear={() => ws.setItems([])}
            maxHeightClass="max-h-[70vh]"
            groupLabel={LABEL_AGRUPAMENTO(ws.item?.portalOrigem)}
          />
        </div>
      ) : (
        <div className={`${SURFACE_CENTRAL.card} p-8 text-center space-y-4`}>
          <div>
            <div className="text-slate-300 text-sm font-medium">Nenhum item na proposta ainda</div>
            <p className="text-xs text-slate-500 mt-1">
              Traga os itens do portal (ou monte manualmente) para começar a precificar.
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={actions.onLoadItems}
              disabled={busy}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name={busy ? 'refresh' : 'download'} className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
              {busy ? 'Carregando itens…' : 'Carregar itens do portal'}
            </button>
            <span className="text-xs text-slate-500">ou</span>
            <button
              onClick={() => ws.setItems([{
                itemNumber: 1, description: '', quantity: 1, unit: 'UN',
                estimatedPrice: 0, unitPrice: '', brand: '', model: '',
              } as any])}
              className="px-4 py-2 bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-200 rounded-lg text-sm transition-colors"
            >
              + Item manual
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
